import { orgSelectors } from 'ducks/orgs'
import { SellerIntegrationType } from 'opensolar-common'
import { useCallback, useMemo } from 'react'
import { useSelector, useStore } from 'react-redux'
import { RootState } from 'types/state'

export const manualCostIntegration = {
  id: 'component',
  filterKey: 'component',
  name: 'Manual Cost',
  isEnabled: true,
  isConnected: true,
} as SellerIntegrationType

const useEnabledHardwareSuppliers = (sortByPreference: boolean = false) => {
  const enabledSuppliers = useSelector(orgSelectors.getEnabledSellerIntegrationsConfig)
  const supplierFilterKeys = useSelector(orgSelectors.getEnabledHardwareSuppliers)
  const store = useStore<RootState>()
  const supplierPreferences = useSelector(orgSelectors.getOrgSupplierPreferences)

  // Memoized helper to get preference order using current state
  const getSupplierPreferenceOrder = useCallback(
    (filterKey: string) => orgSelectors.getSupplierPreferenceOrder(store.getState(), filterKey),
    [store, supplierPreferences]
  )

  const sortByPreferenceOrder = useCallback(
    (a: string, b: string) => {
      const orderA = getSupplierPreferenceOrder(a) ?? Number.MAX_SAFE_INTEGER
      const orderB = getSupplierPreferenceOrder(b) ?? Number.MAX_SAFE_INTEGER
      return orderA - orderB
    },
    [getSupplierPreferenceOrder]
  )

  const sortSuppliers = useCallback(
    (suppliers: SellerIntegrationType[]): SellerIntegrationType[] | string[] => {
      if (suppliers.length === 0) return suppliers
      if ('filterKey' in suppliers[0]) {
        return (suppliers as SellerIntegrationType[]).sort((a, b) => sortByPreferenceOrder(a.filterKey, b.filterKey))
      }
      return suppliers
    },
    [sortByPreferenceOrder]
  )

  const filteredSuppliers = useMemo(() => {
    const _enabledSuppliers = enabledSuppliers.filter(Boolean)

    if (sortByPreference) {
      const sortedEnabledSupplier = sortSuppliers([..._enabledSuppliers, manualCostIntegration]).map(
        (supplier) => supplier.filterKey
      )
      return sortedEnabledSupplier
    }

    return supplierFilterKeys
  }, [enabledSuppliers, supplierFilterKeys, sortByPreference, sortSuppliers])

  return { filteredSuppliers }
}

export default useEnabledHardwareSuppliers